import React, { useContext, useEffect, useState } from 'react';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import Modal from '../../../../../VentoryUI/components/common/Modal';
import { t } from '../../../../../types/translation/Translator';
import { OrderRequest } from '../../../../../requests/order.request';
import { useRequest } from '../../../../../requests/common/requestHook';
import { ProductTransactionRequest } from '../../../../../requests/productTransaction.request';
import { Order, OrderInput, OrderParentType, OrderUtil } from '../../../../../types/order';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { ProductTransactionContext } from '../../../../../context/ProductTransactionContext';
import { OrderContext } from '../../../../../context/OrderContext';
import { ProductTransaction, ProductTransactionSpecifiers } from '../../../../../types/productTransaction';
import CreateOrderInfoPane from './Panes/CreateOrderInfoPane';
import CreateOrderProductInfoPane from './Panes/CreateOrderProductInfoPane';
import { DomUtils } from '../../../../../util/dom.util';
import { OrderFlowContext } from '../../../../../context/OrderFlowContext';
import { toMap } from '../../../../../util/map.util';
import { useNavigate } from 'react-router-dom';
import { Routing } from '../../../../../util/routes.util';

interface IssueReturnModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  order: Order;
}

enum CreateOrderState {
  info = 'info',
  product = 'product',
}

export default function IssueReturnModal({ open, setOpen, order }: IssueReturnModalProps) {
  const navigate = useNavigate();

  const { currentCompany } = useContext(CompanyContext);
  const { orders, setOrders } = useContext(OrderContext);
  const { productTransactions, setProductTransactions } = useContext(ProductTransactionContext);

  const { orderFlows } = useContext(OrderFlowContext);

  const [currentPane, setCurrentPane] = useState<CreateOrderState>(CreateOrderState.info);

  const products = Array.from(order.products) as any as ProductTransactionSpecifiers[];

  const [orderInput, setOrderInput] = useState<OrderInput>(
    new OrderInput({
      companyId: currentCompany.id,
      fromStockLocationId: undefined,
      toStockLocationId: order.fromStockLocationId,
      products: undefined,
      parentId: order.id,
      parentType: OrderParentType.order,
    }),
  );

  const [create, { loading }] = useRequest(OrderRequest.create);
  const [loadProductTransactions, { loading: productTransactionsLoading }] = useRequest(ProductTransactionRequest.list);

  const handleCreate = async () => {
    orderInput.products.forEach(p => (p.fromStockLocationId = undefined));
    const result = await create({ data: [orderInput.forCreate()] });
    if (!result) return;

    const items = result;
    for (const order of items) {
      orders.set(order.id, new Order(order));
    }

    setOrders(new Map(orders));
    await fetchProductTransactions();
    // setOpen(false);
    navigate(Routing.operations.orders.overview());
  };

  const fetchProductTransactions = async () => {
    const response = await loadProductTransactions({ page: 0 });
    if (!response) return;

    const items = response.data;
    for (const item of items) {
      productTransactions.set(item.id, new ProductTransaction(item));
    }
    setProductTransactions(new Map(productTransactions));
  };

  const handleNext = () => {
    switch (currentPane) {
      case CreateOrderState.info:
        return setCurrentPane(CreateOrderState.product);
      case CreateOrderState.product:
        handleFinish();
        return;
    }
  };

  const handleFinish = async () => {
    await handleCreate();
  };

  const handleBack = () => {
    switch (currentPane) {
      case CreateOrderState.info:
        return;
      case CreateOrderState.product:
        return setCurrentPane(CreateOrderState.info);
    }
  };

  useEffect(() => {
    if (!orderInput.flowId) return;

    const flow = orderFlows.get(order.flowId);
    if (!flow) return;

    const fields = Array.from(order.customFields.values()).filter(f => flow.customFieldIds.includes(f.id));
    fields.forEach(field => {
      field.generated = true;
    });
    orderInput.customFields = toMap(fields, 'id');
    orderInput.products = toMap(products, p => OrderUtil.orderSpecifierHash(p));
    setOrderInput(new OrderInput(orderInput));
  }, [orderInput.flowId]);

  const modalHeight = DomUtils.getModalHeight();
  const content = () => {
    // TODO: DIRTY! as this is for NWR demo only
    const materialsReturn = 'cdc9e3a4-a078-4e96-bf28-621cddc0c4f0';
    const returnToRDC = 'c1eab927-f4f6-4fb0-bd12-21ac6e2e4351';
    const salesOrderReturn = '94ee9cbd-8076-49ea-8715-df2c48c82f28';
    const receiptOfRepair = '6bf27434-8c00-4a91-9f48-6ad52054ba32';
    const repairOrderToRDC = '18bd6d15-c1ca-4b37-8472-499d29e92097';

    const flowIds = [materialsReturn, returnToRDC, salesOrderReturn, receiptOfRepair, repairOrderToRDC];

    switch (currentPane) {
      case CreateOrderState.info:
        return <CreateOrderInfoPane flowIds={flowIds} order={orderInput} setOrder={setOrderInput} next={handleNext} />;
      case CreateOrderState.product:
        return (
          <CreateOrderProductInfoPane
            order={orderInput}
            setOrder={setOrderInput}
            setError={() => {}}
            next={handleNext}
            back={handleBack}
            loading={loading}
            validContainers={[]}
            modalHeight={modalHeight}
            disabled={true}
          />
        );
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={t(TranslationKey.issueReturn).singular.label}
      height={modalHeight}
    >
      {content()}
    </Modal>
  );
}
